/* eslint-disable sonarjs/no-duplicate-string,no-param-reassign,no-underscore-dangle */
/* eslint no-unused-vars:0 */
/* eslint no-undef:0 */
import axios from 'axios';
import NProgress from 'nprogress';
import cookie from 'vue-cookies';
import nonResidentAuthService from '@/services/nonResidentAuth';
import { showApiErrorToast } from '@/utils/apiErrorToast';
import { shouldSkipGlobalErrorRedirect } from '@/utils/portalJourney';
import { USER_TYPE } from '@/utils/const';
import { setupPortalCacheInvalidation } from '@/utils/portalBootstrap';
import {
  attachAuthHeader,
  buildSignInReturnUrl,
  isGuestAuthRequest,
  isMapfreRequest,
  isTokenExpired,
} from '@/utils/authSession';
import { persistReferralAgentCode, getReferralAgentCode } from '@/services/referralApi';

async function ensureFreshAuthToken(store) {
  const token = store.getters['auth/accessToken'];
  if (!token || !isTokenExpired(token)) {
    return token;
  }
  await store.dispatch('auth/refresh');
  return store.getters['auth/accessToken'];
}

async function redirectUnauthenticated(router, store) {
  const returnUrl = buildSignInReturnUrl(cookie, USER_TYPE);
  await store.dispatch('auth/signOut');
  router.push({ name: 'SignIn', query: { r: returnUrl } });
}

export default {
  init(router, store) {
    router.beforeEach((to, _from, next) => {
      const agentCode = to.query && to.query.agentCode;
      if (agentCode && typeof agentCode === 'string' && agentCode.trim()) {
        // Persist immediately so redirects / login do not lose the code.
        persistReferralAgentCode(agentCode.trim());
      }
      // Revalidate whenever a referral code exists (URL, cookie, or snapshot) so product
      // navigations that drop ?agentCode= do not lose the linked agent.
      if (getReferralAgentCode(to.query)) {
        store.dispatch('referral/ensureReferralValidated', to.query).catch(() => {});
      } else {
        store.dispatch('referral/hydrateFromStorage').catch(() => {});
      }
      next();
    });

    axios.interceptors.request.use(async (config) => {
      NProgress.configure({ showSpinner: false });
      NProgress.start();

      if (!isMapfreRequest(config)) {
        if (store.getters['auth/isAuthenticated']) {
          try {
            const token = await ensureFreshAuthToken(store);
            attachAuthHeader(config, token);
          } catch (e) {
            await redirectUnauthenticated(router, store);
            return Promise.reject(e);
          }
        } else if (isGuestAuthRequest(config)) {
          try {
            const authHeaders = await nonResidentAuthService.getAuthHeaders();
            config.headers.Auth = authHeaders.Auth;
            config.headers['Content-Type'] = authHeaders['Content-Type'];
          } catch (error) {
            // Let the request proceed; caller handles failure.
          }
        }
      }

      if (process.env.NODE_ENV === 'production') config.baseURL = '/api';
      return config;
    });

    axios.interceptors.response.use((response) => {
      NProgress.done();
      const { data, config } = response;
      const isGet = config.method === 'get';
      const skipError = config._skipErrorRedirect;
      const isNotif = config.url.includes('notification');
      const isQuotes = config.url.includes('clientQuotes');
      const softFail = skipError || shouldSkipGlobalErrorRedirect(router, store);

      if (typeof data !== 'string' && data.success === false && isGet && !softFail && !isNotif && !isQuotes) {
        router.push({ name: 'NotFound' });
      }
      return response;
    });

    axios.interceptors.response.use(null, async (error) => {
      NProgress.done();

      const skipPageRedirect = error.config?._skipErrorRedirect
        || shouldSkipGlobalErrorRedirect(router, store);

      if (error.response) {
        if (error.response.status === 401) {
          const originalConfig = error.config || {};
          const canRetryWithCognito = store.getters['auth/isAuthenticated']
            && !originalConfig._authRetried
            && !isGuestAuthRequest(originalConfig)
            && !isMapfreRequest(originalConfig);

          if (canRetryWithCognito) {
            originalConfig._authRetried = true;
            try {
              await store.dispatch('auth/refresh');
              attachAuthHeader(originalConfig, store.getters['auth/accessToken']);
              return axios(originalConfig);
            } catch (e) {
              await redirectUnauthenticated(router, store);
              return Promise.reject(error);
            }
          }
        }

        if (skipPageRedirect) {
          // On in-app journeys (customer + agent), suppress global error toasts by default.
          // Callers that want a global toast must opt in with _showErrorToast: true.
          if (error.config?._showErrorToast === true) {
            showApiErrorToast(error);
          }
          return Promise.reject(error);
        }

        const isAgentJourney = router.currentRoute.path.startsWith('/agent');
        if ([403, 500, 502, 504].includes(error.response.status) && !isAgentJourney) {
          router.push({ name: 'ServerError' });
        }
        if (error.response.status === 404) {
          const url = error.response.config.url || '';
          const skip404 = url.includes('evaluation/status') || url.includes('agency/referral') || isAgentJourney;
          if (!skip404) {
            router.push({ name: 'NotFound' });
          }
        }
      } else if (!skipPageRedirect && !router.currentRoute.path.startsWith('/agent')) {
        router.push({ name: 'NoInternet' });
      } else if (skipPageRedirect && error.config?._showErrorToast === true) {
        showApiErrorToast(error);
      }

      return Promise.reject(error);
    });

    router.beforeResolve((to, from, next) => {
      if (to.path) {
        NProgress.configure({ showSpinner: false });
        NProgress.start();
      }
      next();
    });

    router.afterEach(() => {
      NProgress.done();
    });

    setupPortalCacheInvalidation(store, router);
  },
};
